import React, { useState, useEffect } from 'react';
import { ChevronDown, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import mockData from '../mock';

export const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="arven-container">
      {/* Navigation */}
      <nav className={`arven-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-content">
          <div className="nav-logo">ARVEN ARCHITECTS</div>
          <div className="nav-links">
            <button onClick={() => scrollToSection('products')} className="nav-link">Collection</button>
            <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
            <button onClick={() => scrollToSection('craft')} className="nav-link">Philosophy</button>
            <button onClick={() => window.location.href = '/shop'} className="nav-link">Shop</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Video */}
      <section className="hero-section">
        <div className="hero-video-container">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="hero-video"
          >
            <source src={mockData.heroVideo} type="video/mp4" />
          </video>
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <p className="hero-subtitle">
            Elevation reimagined through sculptural form
          </p>
          <p className="hero-description">
            Where material authenticity converges with spatial consciousness.
            <br />
            Furniture as vessels of experience—witnesses to daily existence.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products" className="products-section">
        <div className="section-header">
          <h2 className="section-title">Collection</h2>
          <p className="section-subtitle">Height-adjustable desks in Scandinavian materiality</p>
        </div>

        <div className="products-grid">
          {mockData.products.map((product, index) => (
            <div
              key={product.id}
              className={`product-card ${activeProduct === product.id ? 'active' : ''}`}
              onMouseEnter={() => setActiveProduct(product.id)}
              onMouseLeave={() => setActiveProduct(null)}
            >
              <div className="product-image-wrapper">
                <img
                  src={product.image}
                  alt={product.name}
                  className="product-image"
                  loading="lazy"
                />
                <div className="product-overlay">
                  <Button className="product-btn">Explore</Button>
                </div>
              </div>
              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">{product.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Collections */}
      <section className="featured-section">
        <div className="featured-grid">
          {mockData.featured.map((item, index) => (
            <div key={index} className={`featured-item ${item.height}`}>
              <div className="featured-image-wrapper">
                {item.video ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="featured-image"
                  >
                    <source src={item.video} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="featured-image"
                    loading="lazy"
                  />
                )}
                {item.overlay && (
                  <div className="featured-overlay">
                    <div className="featured-overlay-text">{item.overlay}</div>
                  </div>
                )}
              </div>
              <div className="featured-content">
                <h3 className="featured-title">{item.title}</h3>
                <p className="featured-description">{item.description}</p>
                <p className="featured-meta">{item.meta}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="about-content">
          <div className="about-text">
            <h2 className="section-title">About Arven</h2>
            <p className="about-description">
              Arven Architects was conceived from a profound questioning of the contemporary workspace—a rejection
              of the anonymous, the mass-produced, the soulless. We believe that objects inhabiting our daily
              environments should possess a deeper resonance, a presence that honors both maker and inhabitant.
            </p>
            <p className="about-description">
              Every height-adjustable desk emerges through meticulous hand-craftsmanship, where generations of
              woodworking knowledge meet contemporary ergonomic understanding. We work exclusively with natural
              materials, allowing the inherent character of each timber grain to guide our creative process,
              ensuring that no two pieces share an identical narrative.
            </p>
          </div>
          <div className="about-image">
            <img
              src={mockData.aboutImage}
              alt="Arven craftsmanship"
              className="about-img"
            />
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="craft" className="philosophy-section">
        <div className="philosophy-header">
          <h2 className="section-title">Our Philosophy</h2>
        </div>
        <div className="philosophy-content">
          <p className="philosophy-text">
            At Arven Architects, we conceive furniture as spatial interventions—meditations on materiality, temporality, 
            and the human experience of inhabiting space. Our design philosophy emanates from Scandinavian architectural 
            tradition, where reductionism encounters poetry, and where every gesture is imbued with intention.
            <br /><br />
            We engage exclusively with materials sourced from responsibly managed forests, shaped by artisan hands whose 
            decades of wisdom understand wood as a living archive. Each piece becomes a singular expression—resisting 
            industrial homogenization through variance in grain, tonality, and light response. These are not imperfections 
            but affirmations of origin, celebrations of natural diversity.
            <br /><br />
            We practice 'slow architecture'—a deliberate resistance to accelerated consumption cycles. Through certified 
            timber and plant-based treatments, we ensure our process respects both craft tradition and ecological 
            responsibility. We aspire to create furniture that accumulates meaning across generations, becoming quiet 
            companions to evolving lives. This is our inheritance, our arv, offered to the future.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="footer-content">
          <div className="footer-logo">ARVEN ARCHITECTS</div>
          <div className="footer-origin">
            <svg className="denmark-flag" width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="24" height="18" fill="#C8102E"/>
              <rect x="7" width="3" height="18" fill="white"/>
              <rect y="7.5" width="24" height="3" fill="white"/>
            </svg>
            <span className="footer-origin-text">Designed & produced in Denmark</span>
          </div>
          <p className="footer-tagline">
            Handcrafted height-adjustable desks in Scandinavian design
          </p>
          
          <div className="footer-columns">
            <div className="footer-column">
              <h3 className="footer-column-title">Contact</h3>
              <div className="footer-contact">
                <div className="footer-contact-item">
                  <Mail size={16} />
                  <span>contact@arven.dk</span>
                </div>
                <div className="footer-contact-item">
                  <Phone size={16} />
                  <span>+45 12 34 56 78</span>
                </div>
                <div className="footer-contact-item">
                  <MapPin size={16} />
                  <span>Copenhagen, Denmark</span>
                </div>
              </div>
            </div>
            
            <div className="footer-column">
              <h3 className="footer-column-title">Showroom</h3>
              <p className="footer-info">
                By appointment
              </p>
              <p className="footer-info">
                Mon-Fri: 10:00 - 17:00
                <br />
                Sat: 11:00 - 15:00
              </p>
            </div>

            <div className="footer-column">
              <h3 className="footer-column-title">Services</h3>
              <p className="footer-info">
                Bespoke consultation
              </p>
              <p className="footer-info">
                Custom solutions
              </p>
              <p className="footer-info">
                Delivery & installation
              </p>
            </div>
          </div>
          
          <p className="footer-copyright">© 2025 Arven Architects. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;